/* ==========================================================================
   AMBIENT HOME — Trouvez votre ambiance (Shop the look)
   Fichier : assets/ah-ambiance.js
   - <ahi-grid> : filtres, chargement progressif, favoris, vidéos
   - <ahi-look> : galerie, variantes, ajout au panier, « tout le look »
   ========================================================================== */
(function () {
  'use strict';

  if (window.AHAmbiance) return;

  var doc = document;
  var root = doc.documentElement;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  /* ------------------------------------------------------------------------
     Utilitaires
     ------------------------------------------------------------------------ */

  function raf(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; fn(); });
    };
  }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '{{amount_with_comma_separator}}€';
    format = String(format).replace(/<[^>]*>/g, '');
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount_with_comma_separator';
    var value = (Number(cents) || 0) / 100;

    function fmt(decimals, thousands, decimal) {
      var parts = value.toFixed(decimals).split('.');
      var int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? int + decimal + parts[1] : int;
    }

    var out;
    switch (key) {
      case 'amount': out = fmt(2, ',', '.'); break;
      case 'amount_no_decimals': out = fmt(0, ',', '.'); break;
      case 'amount_no_decimals_with_comma_separator': out = fmt(0, '.', ','); break;
      case 'amount_with_space_separator': out = fmt(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = fmt(0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': out = fmt(2, "'", '.'); break;
      default: out = fmt(2, '.', ',');
    }
    return match ? format.replace(match[0], out) : out;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Hauteur du header collant du thème → variables CSS */
  var Header = {
    init: function () {
      if (this.ready) return;
      this.ready = true;
      this.update = this.update.bind(this);
      var onScroll = raf(this.update);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      var section = this.section();
      if (section && 'ResizeObserver' in window) new ResizeObserver(onScroll).observe(section);
      this.update();
    },
    section: function () {
      return doc.querySelector('.shopify-section-header') || doc.querySelector('sticky-header');
    },
    update: function () {
      var section = this.section();
      var height = 0;
      var top = 0;
      if (section && doc.querySelector('sticky-header')) {
        /* Partie du header qui reste visible une fois collée en haut */
        var bar = section.querySelector('.header__top');
        var bottom = section.querySelector('.header__bottom');
        height = (bar || section).getBoundingClientRect().height;
        if (bar && bottom && window.innerWidth < 1100 && getComputedStyle(bottom).display !== 'none') {
          height += bottom.getBoundingClientRect().height;
        }
        height = Math.round(height);
        var hidden = section.classList.contains('shopify-section-header-hidden');
        top = hidden ? 0 : height;
      }
      if (height !== this.h) { this.h = height; root.style.setProperty('--ahi-header-h', height + 'px'); }
      if (top !== this.t) { this.t = top; root.style.setProperty('--ahi-sticky-top', top + 'px'); }
    }
  };

  /* Favoris (enregistrés dans le navigateur du client) */
  var Saved = {
    key: 'ahi:saved:v1',
    read: function () {
      try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (e) { return []; }
    },
    write: function (list) {
      try { localStorage.setItem(this.key, JSON.stringify(list.slice(0, 200))); } catch (e) { /* stockage indisponible */ }
    },
    has: function (handle) { return this.read().indexOf(handle) > -1; },
    toggle: function (handle) {
      var list = this.read();
      var i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1); else list.unshift(handle);
      this.write(list);
      var saved = i === -1;
      doc.dispatchEvent(new CustomEvent('ahi:saved', { detail: { handle: handle, saved: saved } }));
      return saved;
    },
    sync: function (scope) {
      var list = this.read();
      (scope || doc).querySelectorAll('[data-ahi-save]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', list.indexOf(btn.getAttribute('data-ahi-save')) > -1 ? 'true' : 'false');
      });
    }
  };

  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ahi-save]');
    if (!btn) return;
    e.preventDefault();
    var saved = Saved.toggle(btn.getAttribute('data-ahi-save'));
    Saved.sync();
    btn.classList.remove('is-pop');
    void btn.offsetWidth;
    btn.classList.add('is-pop');
    Toast.show(saved ? (btn.getAttribute('data-saved-msg') || 'Ambiance enregistrée') : (btn.getAttribute('data-removed-msg') || 'Ambiance retirée de vos favoris'));
  });

  /* Petit message en bas d'écran */
  var Toast = {
    show: function (message, opts) {
      opts = opts || {};
      if (!this.el) {
        this.el = doc.createElement('div');
        this.el.className = 'ahi-toast';
        this.el.setAttribute('role', 'status');
        this.el.setAttribute('aria-live', 'polite');
        doc.body.appendChild(this.el);
      }
      var html = '<span>' + escapeHtml(message) + '</span>';
      if (opts.link) html += '<a href="' + escapeHtml(opts.link) + '">' + escapeHtml(opts.linkLabel || 'Voir') + '</a>';
      this.el.innerHTML = html;
      this.el.classList.toggle('is-error', !!opts.error);
      var el = this.el;
      requestAnimationFrame(function () { el.classList.add('is-visible'); });
      clearTimeout(this.timer);
      this.timer = setTimeout(function () { el.classList.remove('is-visible'); }, opts.duration || 3200);
    }
  };

  /* Fondu des images au chargement */
  function watchImages(scope) {
    scope.querySelectorAll('img.ahi-img').forEach(function (img) {
      if (img.complete && img.naturalWidth) { img.classList.add('is-loaded'); return; }
      var done = function () { img.classList.add('is-loaded'); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
    scope.classList.add('is-js');
  }

  /* Lecture automatique des vidéos visibles (muettes) */
  var videoObserver = null;
  function watchVideos(scope) {
    var videos = scope.querySelectorAll('video.ahi-video');
    if (!videos.length) return;
    videos.forEach(function (v) {
      v.muted = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.removeAttribute('controls');
    });
    if (reduceMotion || saveData || !('IntersectionObserver' in window)) return;
    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (v.hasAttribute('data-ahi-manual')) return;
          if (entry.isIntersecting && entry.intersectionRatio >= .35) {
            if (v.preload === 'none') v.preload = 'auto';
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: [0, .35, .7] });
    }
    videos.forEach(function (v) { videoObserver.observe(v); });
  }

  /* ------------------------------------------------------------------------
     Panier : ajout + ouverture du tiroir panier du thème
     ------------------------------------------------------------------------ */

  var Cart = {
    url: function () {
      var base = (window.routes && window.routes.cart_add_url) || '/cart/add';
      return base.replace(/\.js$/, '') + '.js';
    },
    cartUrl: function () { return (window.routes && window.routes.cart_url) || '/cart'; },
    drawer: function () { return doc.querySelector('cart-drawer'); },
    post: function (items, withSections) {
      var body = { items: items };
      var drawer = this.drawer();
      if (withSections && drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; }).join(',');
        body.sections_url = window.location.pathname;
      }
      return fetch(this.url(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok || data.status) {
            var err = new Error(data.description || data.message || 'Impossible d’ajouter cet article.');
            err.data = data;
            throw err;
          }
          return data;
        });
      });
    },
    open: function (data, message) {
      var drawer = this.drawer();
      if (typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try { window.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ah-ambiance' }); } catch (e) { /* noop */ }
      }
      if (drawer && data && data.sections && typeof drawer.renderContents === 'function') {
        try {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
          return;
        } catch (e) { /* on passe au message */ }
      }
      Toast.show(message || 'Ajouté au panier', { link: this.cartUrl(), linkLabel: 'Voir le panier' });
    },
    /* Ajoute une liste ; si l'envoi groupé échoue, on tente article par article */
    addMany: function (items) {
      var self = this;
      return self.post(items, true).then(function (data) {
        return { data: data, added: items.length, failed: 0 };
      }).catch(function (err) {
        if (items.length < 2) throw err;
        var added = 0;
        var failed = 0;
        var lastData = null;
        var chain = Promise.resolve();
        items.forEach(function (item) {
          chain = chain.then(function () {
            return self.post([item], true).then(function (d) { added++; lastData = d; }).catch(function () { failed++; });
          });
        });
        return chain.then(function () {
          if (!added) throw err;
          return { data: lastData, added: added, failed: failed };
        });
      });
    }
  };

  /* ------------------------------------------------------------------------
     Offre « ambiance complète » : -X % quand toutes les pièces sont au panier
     (la synchronisation du code se fait dans snippets/ah-pack-sync.liquid)
     ------------------------------------------------------------------------ */

  var Pack = {
    config: function (el) {
      var code = String(el.getAttribute('data-ahi-pack') || '').trim().toUpperCase();
      if (!code) return null;
      var ids = String(el.getAttribute('data-ahi-pack-ids') || '').split(',')
        .map(function (id) { return id.trim(); })
        .filter(Boolean);
      if (ids.length < 2) return null;
      return { code: code, ids: ids, pct: parseFloat(el.getAttribute('data-ahi-pack-pct')) || 15 };
    },
    /* Suit l'ambiance dans le panier et applique son code si elle est complète */
    apply: function (cfg) {
      if (!cfg || !window.AHPack || typeof window.AHPack.sync !== 'function') return Promise.resolve(null);
      var register = {};
      register[cfg.code] = cfg.ids;
      var opts = { register: register, silent: true };
      var drawer = Cart.drawer();
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        opts.sections = drawer.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      }
      return window.AHPack.sync(opts).catch(function () { return null; });
    },
    isActive: function (cart, cfg) {
      return ((cart && cart.discount_codes) || []).some(function (d) {
        var code = typeof d === 'string' ? d : d && d.code;
        return String(code || '').toUpperCase() === cfg.code && (typeof d === 'string' || d.applicable !== false);
      });
    },
    announce: function (cart) {
      if (cart && window.AHPack && typeof window.AHPack.announce === 'function') window.AHPack.announce(cart);
    }
  };

  function setLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------------
     <ahi-grid> — la grille d'ambiances
     ------------------------------------------------------------------------ */

  var SAVED_KEY = 'enregistrees';

  function AhiGridInit(el) {
    var list = el.querySelector('[data-ahi-list]');
    if (!list) return;

    var tiles = Array.prototype.slice.call(list.querySelectorAll('[data-ahi-tile]'));
    var tabs = Array.prototype.slice.call(el.querySelectorAll('[data-ahi-filter]'));
    var savedTab = el.querySelector('[data-ahi-filter="' + SAVED_KEY + '"]');
    var bar = el.querySelector('[data-ahi-filters]');
    var scroller = el.querySelector('[data-ahi-tabs]');
    var ink = el.querySelector('[data-ahi-ink]');
    var more = el.querySelector('[data-ahi-more]');
    var moreBtn = el.querySelector('[data-ahi-more-btn]');
    var progressText = el.querySelector('[data-ahi-progress-text]');
    var progressFill = el.querySelector('[data-ahi-progress-fill]');
    var sentinel = el.querySelector('[data-ahi-sentinel]');
    var empty = el.querySelector('[data-ahi-empty]');

    var initial = parseInt(el.getAttribute('data-initial'), 10) || 16;
    var batch = parseInt(el.getAttribute('data-batch'), 10) || 12;
    var max = parseInt(el.getAttribute('data-max'), 10) || 0;
    var infinite = el.getAttribute('data-infinite') === 'true' && !max;
    var useUrl = el.hasAttribute('data-url-sync');
    var shown = initial;
    var filter = 'all';
    var visibleSet = new Set();

    function matches(tile) {
      if (filter === 'all') return true;
      if (filter === SAVED_KEY) return Saved.has(tile.getAttribute('data-handle'));
      return (tile.getAttribute('data-rooms') || '').indexOf('|' + filter + '|') > -1;
    }

    function apply(animate) {
      var matched = tiles.filter(matches);
      var limit = max ? Math.min(max, matched.length) : Math.min(shown, matched.length);
      var next = new Set(matched.slice(0, limit));
      var k = 0;

      tiles.forEach(function (tile) {
        var on = next.has(tile);
        if (on) {
          var wasVisible = visibleSet.has(tile);
          tile.hidden = false;
          if (animate && !wasVisible && !reduceMotion) {
            tile.classList.remove('is-in');
            tile.style.setProperty('--i', Math.min(k++, 11));
            void tile.offsetWidth;
            tile.classList.add('is-in');
          }
        } else {
          tile.hidden = true;
          tile.classList.remove('is-in');
        }
      });
      visibleSet = next;

      if (empty) empty.hidden = matched.length > 0;

      if (more) {
        var remaining = matched.length - limit;
        var showMore = !max && remaining > 0;
        more.hidden = !showMore;
        if (progressText) progressText.textContent = limit + ' / ' + matched.length;
        if (progressFill) progressFill.style.setProperty('--p', matched.length ? (limit / matched.length).toFixed(3) : 0);
        if (moreBtn) moreBtn.hidden = !showMore;
      }
    }

    function moveInk(instant) {
      if (!ink) return;
      var active = tabs.filter(function (t) { return t.getAttribute('aria-pressed') === 'true' && !t.hidden; })[0];
      if (!active) { ink.style.opacity = '0'; return; }
      ink.classList.toggle('no-anim', !!instant);
      ink.style.opacity = '1';
      ink.style.setProperty('--ink-w', active.offsetWidth + 'px');
      ink.style.setProperty('--ink-x', active.offsetLeft + 'px');
      if (instant) requestAnimationFrame(function () { ink.classList.remove('no-anim'); });
    }

    function centerTab(tab, smooth) {
      if (!scroller || !tab) return;
      var target = tab.offsetLeft - (scroller.clientWidth - tab.offsetWidth) / 2;
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      scroller.scrollTo({ left: Math.max(0, target), behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    }

    function checkOverflow() {
      if (scroller) scroller.classList.toggle('is-overflowing', scroller.scrollWidth > scroller.clientWidth + 2);
    }

    function updateCounts() {
      tabs.forEach(function (tab) {
        var c = tab.querySelector('[data-ahi-count]');
        var key = tab.getAttribute('data-ahi-filter');
        var n;
        if (key === 'all') n = tiles.length;
        else if (key === SAVED_KEY) n = tiles.filter(function (t) { return Saved.has(t.getAttribute('data-handle')); }).length;
        else n = tiles.filter(function (t) { return (t.getAttribute('data-rooms') || '').indexOf('|' + key + '|') > -1; }).length;
        if (c) c.textContent = n;
        if (key === SAVED_KEY) tab.hidden = n === 0;
      });
    }

    function syncUrl() {
      if (!useUrl || !window.history || !history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        if (filter === 'all') url.searchParams.delete('piece'); else url.searchParams.set('piece', filter);
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      } catch (e) { /* noop */ }
    }

    function setFilter(key, opts) {
      opts = opts || {};
      if (!tabs.some(function (t) { return t.getAttribute('data-ahi-filter') === key && !t.hidden; })) key = 'all';
      filter = key;
      shown = initial;
      tabs.forEach(function (t) { t.setAttribute('aria-pressed', t.getAttribute('data-ahi-filter') === key ? 'true' : 'false'); });
      moveInk(opts.instant);
      var active = tabs.filter(function (t) { return t.getAttribute('data-ahi-filter') === key; })[0];
      centerTab(active, !opts.instant);
      visibleSet = new Set();
      apply(!opts.instant || opts.animate);
      if (!opts.instant) {
        syncUrl();
        /* Si la barre est collée en haut, on remonte au début de la grille */
        if (bar && bar.classList.contains('is-stuck')) {
          var top = list.getBoundingClientRect().top + window.pageYOffset - bar.offsetHeight - (parseFloat(getComputedStyle(root).getPropertyValue('--ahi-sticky-top')) || 0);
          window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }

    function loadMore() {
      shown += batch;
      apply(true);
      requestAnimationFrame(checkSentinel);
    }

    function checkSentinel() {
      if (!infinite || !sentinel || more.hidden) return;
      var r = sentinel.getBoundingClientRect();
      if (r.top < window.innerHeight + 700) loadMore();
    }

    /* Évènements */
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var key = tab.getAttribute('data-ahi-filter');
        if (key === filter) return;
        setFilter(key);
      });
    });

    if (moreBtn) moreBtn.addEventListener('click', loadMore);

    var io = null;
    if (infinite && sentinel && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) loadMore();
      }, { rootMargin: '0px 0px 700px 0px' });
      io.observe(sentinel);
    }

    var onScroll = raf(function () {
      if (!bar) return;
      var stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
      var stuck = bar.getBoundingClientRect().top <= stickyTop + .5 && window.pageYOffset > 0;
      bar.classList.toggle('is-stuck', stuck);
    });
    window.addEventListener('scroll', onScroll, { passive: true });

    var onResize = raf(function () { moveInk(true); checkOverflow(); });
    window.addEventListener('resize', onResize, { passive: true });
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(function () { moveInk(true); checkOverflow(); });

    var onSaved = function () {
      updateCounts();
      if (filter === SAVED_KEY) {
        if (savedTab && savedTab.hidden) setFilter('all');
        else { visibleSet = new Set(); apply(false); }
      }
      moveInk();
    };
    doc.addEventListener('ahi:saved', onSaved);

    el._ahiCleanup = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      doc.removeEventListener('ahi:saved', onSaved);
      if (io) io.disconnect();
    };

    /* Démarrage */
    Saved.sync(el);
    updateCounts();
    checkOverflow();
    var startKey = 'all';
    if (useUrl) {
      try { startKey = new URLSearchParams(window.location.search).get('piece') || 'all'; } catch (e) { startKey = 'all'; }
    }
    setFilter(startKey, { instant: true, animate: true });
    watchImages(el);
    watchVideos(el);
    el.classList.add('is-ready');
    el.classList.remove('is-pending');
    onScroll();
  }

  /* ------------------------------------------------------------------------
     Carte produit (page ambiance)
     ------------------------------------------------------------------------ */

  function Card(el, onChange, afterAdd) {
    this.el = el;
    this.onChange = onChange;
    this.afterAdd = afterAdd;
    try {
      this.variants = JSON.parse(el.querySelector('[data-ahi-variants]').textContent) || [];
    } catch (e) { this.variants = []; }
    var currentId = parseInt(el.getAttribute('data-variant-id'), 10);
    this.variant = this.variants.filter(function (v) { return v.id === currentId; })[0] || this.variants[0] || null;
    this.selected = this.variant ? this.variant.o.slice() : [];
    this.options = Array.prototype.slice.call(el.querySelectorAll('[data-ahi-option]'));
    this.price = el.querySelector('[data-ahi-price]');
    this.badge = el.querySelector('[data-ahi-badge]');
    this.btn = el.querySelector('[data-ahi-add]');
    this.img = el.querySelector('.ahl-card__img');
    this.labels = {
      add: el.getAttribute('data-label-add') || 'Ajouter',
      soldout: el.getAttribute('data-label-soldout') || 'Épuisé',
      added: el.getAttribute('data-label-added') || 'Ajouté'
    };
    this.bind();
    this.render(true);
  }

  Card.prototype.bind = function () {
    var self = this;
    this.options.forEach(function (fs) {
      var index = parseInt(fs.getAttribute('data-index'), 10);
      fs.addEventListener('click', function (e) {
        var b = e.target.closest('[data-value]');
        if (!b || !fs.contains(b)) return;
        self.choose(index, b.getAttribute('data-value'));
      });
      var select = fs.querySelector('select');
      if (select) select.addEventListener('change', function () { self.choose(index, select.value); });
    });
    if (this.btn) this.btn.addEventListener('click', function () { self.add(); });
    var alt = this.el.querySelector('.ahl-card__alt');
    if (alt) {
      if (alt.complete && alt.naturalWidth) alt.classList.add('is-loaded');
      else alt.addEventListener('load', function () { alt.classList.add('is-loaded'); }, { once: true });
    }
  };

  Card.prototype.choose = function (index, value) {
    this.selected[index] = value;
    var sel = this.selected;
    var exact = this.variants.filter(function (v) {
      return v.o.every(function (o, i) { return o === sel[i]; });
    })[0];
    if (!exact) {
      /* Combinaison inexistante : on garde la valeur choisie et on prend la meilleure variante */
      var candidates = this.variants.filter(function (v) { return v.o[index] === value; });
      exact = candidates.filter(function (v) { return v.a; })[0] || candidates[0];
      if (exact) this.selected = exact.o.slice();
    }
    if (exact) this.variant = exact;
    this.render(false);
    if (this.onChange) this.onChange();
  };

  Card.prototype.render = function (initial) {
    var v = this.variant;
    if (!v) return;
    var self = this;

    if (!initial && this.price) {
      var sale = v.c > v.p;
      this.price.classList.toggle('is-sale', sale);
      this.price.innerHTML = '<span class="ahl-price__now">' + formatMoney(v.p) + '</span>' +
        (sale ? '<s class="ahl-price__was">' + formatMoney(v.c) + '</s>' : '');
    }

    if (this.badge) {
      var pct = v.c > v.p ? Math.round((v.c - v.p) * 100 / v.c) : 0;
      this.badge.hidden = pct <= 0;
      if (pct > 0) this.badge.textContent = '-' + pct + '%';
    }

    if (!initial && v.i && this.img && this.img.getAttribute('data-src-current') !== v.i) {
      this.img.setAttribute('data-src-current', v.i);
      this.img.removeAttribute('srcset');
      this.img.src = v.i;
    }

    this.options.forEach(function (fs) {
      var index = parseInt(fs.getAttribute('data-index'), 10);
      var label = fs.querySelector('[data-ahi-option-label]');
      if (label) label.textContent = self.selected[index] || '';
      var select = fs.querySelector('select');
      if (select) select.value = self.selected[index];
      fs.querySelectorAll('[data-value]').forEach(function (b) {
        var val = b.getAttribute('data-value');
        b.setAttribute('aria-pressed', val === self.selected[index] ? 'true' : 'false');
        var possible = self.variants.some(function (variant) {
          if (!variant.a || variant.o[index] !== val) return false;
          return variant.o.every(function (o, i) { return i === index || o === self.selected[i]; });
        });
        b.classList.toggle('is-unavailable', !possible);
      });
    });

    if (this.btn) {
      this.btn.disabled = !v.a;
      var lbl = this.btn.querySelector('.ahl-btn__label');
      if (lbl) lbl.textContent = v.a ? this.labels.add : this.labels.soldout;
    }
    this.el.setAttribute('data-variant-id', v.id);
  };

  Card.prototype.add = function () {
    var self = this;
    var v = this.variant;
    if (!v || !v.a || this.btn.classList.contains('is-loading')) return;
    setLoading(this.btn, true);
    Cart.post([{ id: v.id, quantity: 1 }], true).then(function (data) {
      var next = self.afterAdd ? self.afterAdd(data, 'Ajouté au panier') : Promise.resolve({ data: data, message: 'Ajouté au panier' });
      return next.then(function (out) {
        setLoading(self.btn, false);
        self.flash();
        Cart.open(out.data, out.message);
        Pack.announce(out.cart);
      });
    }).catch(function (err) {
      setLoading(self.btn, false);
      Toast.show(err.message, { error: true });
    });
  };

  Card.prototype.flash = function () {
    var lbl = this.btn.querySelector('.ahl-btn__label');
    if (!lbl) return;
    var self = this;
    lbl.textContent = this.labels.added + ' ✓';
    clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(function () { self.render(false); }, 1800);
  };

  /* ------------------------------------------------------------------------
     <ahi-look> — la page d'une ambiance
     ------------------------------------------------------------------------ */

  function AhiLookInit(el) {
    var cleanups = [];

    /* Galerie */
    var gallery = el.querySelector('[data-ahi-gallery]');
    var track = el.querySelector('[data-ahi-track]');
    var dots = Array.prototype.slice.call(el.querySelectorAll('[data-ahi-dot]'));
    var prev = el.querySelector('[data-ahi-prev]');
    var next = el.querySelector('[data-ahi-next]');
    var index = 0;

    if (track) {
      var slides = Array.prototype.slice.call(track.children);
      var setActive = function (i) {
        index = Math.max(0, Math.min(slides.length - 1, i));
        dots.forEach(function (d, k) { d.setAttribute('aria-current', k === index ? 'true' : 'false'); });
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
        slides.forEach(function (s, k) {
          s.setAttribute('aria-hidden', k === index ? 'false' : 'true');
          var v = s.querySelector('video.ahi-video');
          if (!v) return;
          if (k === index) {
            v.removeAttribute('data-ahi-manual');
            if (!reduceMotion && !saveData) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
          } else {
            v.setAttribute('data-ahi-manual', '');
            v.pause();
          }
        });
      };
      var go = function (i) {
        var target = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: target * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        setActive(target);
      };
      var onTrackScroll = raf(function () {
        var w = track.clientWidth || 1;
        var i = Math.round(track.scrollLeft / w);
        if (i !== index) setActive(i);
      });
      track.addEventListener('scroll', onTrackScroll, { passive: true });
      dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k); }); });
      if (prev) prev.addEventListener('click', function () { go(index - 1); });
      if (next) next.addEventListener('click', function () { go(index + 1); });
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); go(index + 1); }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); go(index - 1); }
      });
      var onResize = raf(function () { track.scrollLeft = index * track.clientWidth; });
      window.addEventListener('resize', onResize, { passive: true });
      cleanups.push(function () { window.removeEventListener('resize', onResize); });
      setActive(0);
    }

    /* Lien « voir les articles » (mobile) */
    var jump = el.querySelector('[data-ahi-jump]');
    var head = el.querySelector('#' + (jump ? jump.getAttribute('href').slice(1) : '__none__'));
    if (jump && head) {
      jump.addEventListener('click', function (e) {
        e.preventDefault();
        var offset = parseFloat(getComputedStyle(root).getPropertyValue('--ahi-sticky-top')) || 0;
        var top = head.getBoundingClientRect().top + window.pageYOffset - offset - 12;
        window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }

    /* Partager */
    var share = el.querySelector('[data-ahi-share]');
    if (share) {
      share.addEventListener('click', function () {
        var data = { title: share.getAttribute('data-title') || doc.title, url: window.location.href.split('?')[0] };
        if (navigator.share) {
          navigator.share(data).catch(function () {});
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(data.url).then(function () { Toast.show('Lien copié'); }, function () { Toast.show(data.url); });
        } else {
          Toast.show(data.url, { duration: 6000 });
        }
      });
    }

    /* Produits + barre « tout le look » */
    var bundle = el.querySelector('[data-ahi-bundle]');
    var bundleTotal = el.querySelector('[data-ahi-bundle-total]');
    var bundleBtn = el.querySelector('[data-ahi-bundle-btn]');
    var bundleCount = el.querySelector('[data-ahi-bundle-count]');
    var packHint = el.querySelector('[data-ahi-pack-hint]');
    var pack = Pack.config(el);
    var packLabels = {
      hint: (bundleBtn && bundleBtn.getAttribute('data-label-pack-hint')) || '',
      off: (bundleBtn && bundleBtn.getAttribute('data-label-pack-off')) || '',
      added: (bundleBtn && bundleBtn.getAttribute('data-label-pack-added')) || ''
    };
    var cards = [];

    /* Après un ajout : suit l'ambiance et applique la remise si toutes les pièces sont au panier */
    var afterAdd = function (data, message) {
      if (!pack) return Promise.resolve({ data: data, message: message });
      return Pack.apply(pack).then(function (res) {
        var out = { data: data, message: message, cart: res && res.cart };
        if (res && res.sections) out.data = res;
        if (res && res.added && res.added.length && Pack.isActive(res.cart, pack) && packLabels.added) out.message = packLabels.added;
        return out;
      });
    };

    var updateBundle = function () {
      if (!bundle) return;
      var total = 0;
      var compare = 0;
      var available = 0;
      cards.forEach(function (c) {
        if (!c.variant || !c.variant.a) return;
        available++;
        total += c.variant.p;
        compare += Math.max(c.variant.c || 0, c.variant.p);
      });
      var unavailable = cards.length - available;
      var packReady = !!pack && cards.length > 1 && available === cards.length;
      var packTotal = packReady ? Math.round(total * (100 - pack.pct) / 100) : total;
      if (bundleTotal) {
        if (packReady) {
          bundleTotal.innerHTML = '<span>' + formatMoney(packTotal) + '</span><s>' + formatMoney(Math.max(compare, total)) + '</s>';
        } else {
          bundleTotal.innerHTML = '<span>' + formatMoney(total) + '</span>' + (compare > total ? '<s>' + formatMoney(compare) + '</s>' : '');
        }
      }
      if (pack) {
        bundle.classList.toggle('is-pack-off', !packReady);
        if (packHint) {
          packHint.textContent = packReady
            ? packLabels.hint + ' · -' + formatMoney(total - packTotal)
            : packLabels.off;
        }
      }
      if (bundleCount) {
        var label = available + ' article' + (available > 1 ? 's' : '');
        if (unavailable > 0) label += ' · ' + unavailable + ' indisponible' + (unavailable > 1 ? 's' : '');
        bundleCount.textContent = label;
      }
      if (bundleBtn) bundleBtn.disabled = available === 0;
    };

    el.querySelectorAll('[data-ahi-card]').forEach(function (cardEl) {
      cards.push(new Card(cardEl, updateBundle, afterAdd));
    });
    updateBundle();

    if (bundleBtn) {
      bundleBtn.addEventListener('click', function () {
        if (bundleBtn.classList.contains('is-loading')) return;
        var items = cards.filter(function (c) { return c.variant && c.variant.a; }).map(function (c) { return { id: c.variant.id, quantity: 1 }; });
        if (!items.length) return;
        setLoading(bundleBtn, true);
        Cart.addMany(items).then(function (res) {
          var msg = res.added + ' article' + (res.added > 1 ? 's' : '') + ' ajouté' + (res.added > 1 ? 's' : '') + ' au panier';
          if (res.failed) msg += ' · ' + res.failed + ' indisponible' + (res.failed > 1 ? 's' : '');
          return afterAdd(res.data, msg).then(function (out) {
            setLoading(bundleBtn, false);
            if (res.failed) Toast.show(msg, { link: Cart.cartUrl(), linkLabel: 'Voir le panier', duration: 5000 });
            Cart.open(out.data, out.message);
            Pack.announce(out.cart);
          });
        }).catch(function (err) {
          setLoading(bundleBtn, false);
          Toast.show(err.message, { error: true });
        });
      });
    }

    Saved.sync(el);
    watchImages(el);
    watchVideos(el);
    if (track) {
      /* la vidéo de la 1re slide démarre si visible */
      var first = track.querySelector('video.ahi-video');
      if (first && !reduceMotion && !saveData) { var p = first.play(); if (p && p.catch) p.catch(function () {}); }
    }
    if (gallery) gallery.classList.add('is-ready');

    el._ahiCleanup = function () { cleanups.forEach(function (fn) { fn(); }); };
  }

  /* ------------------------------------------------------------------------
     Custom elements (compatibles avec l'éditeur de thème)
     ------------------------------------------------------------------------ */

  function define(name, init) {
    if (customElements.get(name)) return;
    customElements.define(name, class extends HTMLElement {
      connectedCallback() {
        if (this._ahiReady) return;
        this._ahiReady = true;
        Header.init();
        var self = this;
        try { init(self); } catch (e) {
          self.classList.remove('is-pending');
          if (window.console) console.error('[AH Ambiance]', e);
        }
      }
      disconnectedCallback() {
        if (this._ahiCleanup) this._ahiCleanup();
        this._ahiReady = false;
      }
    });
  }

  define('ahi-grid', AhiGridInit);
  define('ahi-look', AhiLookInit);

  /* ------------------------------------------------------------------------
     Transition fluide vignette → page ambiance (navigateurs compatibles)
     ------------------------------------------------------------------------ */

  function clearNames() {
    doc.querySelectorAll('[data-ahi-vt]').forEach(function (n) { n.style.viewTransitionName = ''; });
    doc.querySelectorAll('[data-ahi-hero]').forEach(function (n) { n.style.viewTransitionName = ''; });
  }

  window.addEventListener('pageswap', function (e) {
    if (!e.viewTransition) return;
    var hero = doc.querySelector('[data-ahi-hero]');
    var dest = null;
    try { dest = new URL(e.activation.entry.url); } catch (err) { dest = null; }
    clearNames();
    if (hero) hero.style.viewTransitionName = 'none';
    if (!dest) return;
    var links = doc.querySelectorAll('[data-ahi-link]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.pathname !== dest.pathname) continue;
      var media = a.querySelector('[data-ahi-vt]');
      if (!media) continue;
      var r = media.getBoundingClientRect();
      if (r.width && r.bottom > 0 && r.top < window.innerHeight) {
        media.style.viewTransitionName = 'ahi-hero';
        break;
      }
    }
  });

  window.addEventListener('pageshow', function (e) { if (e.persisted) clearNames(); });

  window.AHAmbiance = { Saved: Saved, Toast: Toast, Cart: Cart, Pack: Pack, formatMoney: formatMoney };
})();